import React from 'react';
import { Pressable, StyleSheet } from 'react-native';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import { FontSizes } from '@/Utils/Dimensions';
import { formatCurrency } from '@/Utils/CommonUtils';
import { MaterialCommunityIcons } from '@expo/vector-icons';

export type CoachType = 'general' | 'fide_master' | 'international_master' | 'grand_master';

interface CoachSelectorProps {
  selectedCoach: CoachType;
  onSelect: (coach: CoachType) => void;
  isMobile: boolean;
  premiumAmount?: number;
  currency?: string;
  currentCoach?: CoachType; // Current coach from subscription
  coachPerks?: string[]; // Changed to array since it comes from selectedPricing
  availableCoachTiers?: CoachType[]; // Coach tiers that have pricing available
}

const CoachSelector: React.FC<CoachSelectorProps> = ({
  selectedCoach,
  onSelect,
  isMobile,
  premiumAmount,
  currency = 'INR',
  currentCoach = 'general',
  coachPerks: coachPerksProp,
  availableCoachTiers = ['general'], // Default to general if not provided
}) => {
  const getCoachLabel = (coach: CoachType): string => {
    switch (coach) {
      case 'grand_master':
        return 'Grand Masters';
      case 'international_master':
        return 'International Master';
      case 'fide_master':
        return 'FIDE Master';
      default:
        return 'Fide Rated Coach';
    }
  };

  // Get perks for the selected coach from the prop (which comes from selectedPricing)
  const getPerksForCoach = (coach: CoachType): string[] => {
    if (coachPerksProp && coachPerksProp.length > 0) {
      return coachPerksProp;
    }
    // Default perks if not provided from API
    const defaultPerks: Record<CoachType, string[]> = {
      general: [],
      fide_master: [
        'Priority access to preferred time slots',
        'Save up to 20% on your renewal',
        'Expert guidance from FIDE Masters',
        'Personalized learning path',
        'Advanced game analysis',
      ],
      international_master: [
        'Priority access to preferred time slots',
        'Save up to 20% on your renewal',
        'Expert guidance from International Masters',
        'Personalized learning path',
      ],
      grand_master: [
        'Priority access to preferred time slots',
        'Save up to 20% on your renewal',
        'Exclusive access to advanced training modules',
        'Direct mentorship from top-rated Grand Masters',
        'Personalized learning path',
        'Advanced game analysis',
      ],
    };
    return defaultPerks[coach] || [];
  };

  const currentTag=<Box style={{backgroundColor: CCColors.Primary.Aqua, borderRadius: 4, paddingHorizontal: 8, paddingVertical: 4, marginLeft: 8}}><CCText fontSize={FontSizes[12]} fontWeight="bold" color={CCColors.White} >Current</CCText></Box>;
if(availableCoachTiers.length<=1)return null;
  return (
    <Box marginBottom={20}>
      <Box
        backgroundColor={CCColors.White}
        borderRadius={12}
        borderWidth={1}
        borderColor={CCColors.Grey.Divider200}
        overflow="hidden">
        
        {/* Current Coach Display - Unselectable */}
        <Box padding={16} backgroundColor={CCColors.CCTableBg || CCColors.Primary.lightBrown}>
          <CCText 
            fontSize={FontSizes[14]} 
            color={CCColors.TextColor.Dark}
            lineHeight={20}>
            Your coaching is currently set to a <CCText fontWeight="bold" fontSize={FontSizes[16]}>{getCoachLabel(currentCoach)}</CCText>
          </CCText>
        </Box>

        {/* Divider */}
        <Box height={1} backgroundColor={CCColors.Grey.Divider200} />

        {/* Available Coach Upgrades */}
        <Box padding={16}>
          <CCText 
            fontSize={FontSizes[16]} 
            fontWeight="600"
            color={CCColors.TextColor.Dark}
            marginBottom={16}>
            Available coach upgrades
          </CCText>

          {/* General Coach Option */}
          {/* <Pressable 
            onPress={() => onSelect('general')}>
            <Box paddingVertical={12}>
              <Box
                flexDirection="row"
                alignItems="center">
                {selectedCoach === 'general' ? (
                  <Box
                    width={20}
                    height={20}
                    borderRadius={10}
                    backgroundColor={CCColors.Primary.Brown}
                    justifyContent="center"
                    alignItems="center"
                    marginRight={12}>
                    <MaterialCommunityIcons 
                      name="check" 
                      size={12} 
                      color={CCColors.White} 
                    />
                  </Box>
                ) : (
                  <Box
                    width={20}
                    height={20}
                    borderRadius={10}
                    borderWidth={2}
                    borderColor={CCColors.Grey.Grey}
                    marginRight={12}
                  />
                )}
                
                <CCText 
                  fontSize={FontSizes[16]} 
                  fontWeight="500"
                  color={CCColors.TextColor.Dark}>
                  General Coach
                  {currentCoach === 'general' && (
                    currentTag
                  )}
                </CCText>
              </Box>
            </Box>
          </Pressable> */}

          {/* International Master Option - Only show if available */}
          {availableCoachTiers.includes('international_master') && (
            <>
              {/* Divider */}
              <Box height={1} backgroundColor={CCColors.Grey.Divider200} />
              
              <Pressable 
                onPress={() => currentCoach !== 'international_master' && onSelect('international_master')}
                disabled={currentCoach === 'international_master'}>
            <Box paddingVertical={12}>
              <Box
                flexDirection="row"
                alignItems="center"
                opacity={currentCoach === 'international_master' ? 0.5 : 1}>
                {/* Radio Button */}
                {selectedCoach === 'international_master' ? (
                  <Box
                    width={20}
                    height={20}
                    borderRadius={10}
                    backgroundColor={CCColors.Primary.Brown}
                    justifyContent="center"
                    alignItems="center"
                    marginRight={12}>
                    <MaterialCommunityIcons 
                      name="check" 
                      size={12} 
                      color={CCColors.White} 
                    />
                  </Box>
                ) : (
                  <Box
                    width={20}
                    height={20}
                    borderRadius={10}
                    borderWidth={2}
                    borderColor={CCColors.Grey.Grey}
                    marginRight={12}
                  />
                )}
                
                <CCText 
                  fontSize={FontSizes[16]} 
                  fontWeight="500"
                  color={CCColors.TextColor.Dark}>
                  International Master
                  {currentCoach === 'international_master' && (
                    currentTag
                  )}
                </CCText>
              </Box>

              {/* Show perks when selected */}
              {selectedCoach === 'international_master' && (
                <Box marginLeft={32} marginTop={12}>
                  {getPerksForCoach('international_master').map((perk, index) => (
                    <Box key={index} flexDirection="row" alignItems="center" marginBottom={8}>
                      <Box
                        width={16}
                        height={16}
                        borderRadius={8}
                        backgroundColor={CCColors.Green}
                        justifyContent="center"
                        alignItems="center"
                        marginRight={8}>
                        <MaterialCommunityIcons 
                          name="check" 
                          size={10} 
                          color={CCColors.White} 
                        />
                      </Box>
                      <CCText 
                        fontSize={FontSizes[13]} 
                        color={CCColors.TextColor.Dark}
                        flex={1}>
                        {perk}
                      </CCText>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Pressable>
          </>
          )}

          {/* FIDE Master Option - Only show if available */}
          {availableCoachTiers.includes('fide_master') && (
            <>
              {/* Divider */}
              <Box height={1} backgroundColor={CCColors.Grey.Divider200} />
              
              <Pressable 
                onPress={() => currentCoach !== 'fide_master' && onSelect('fide_master')}
                disabled={currentCoach === 'fide_master'}>
            <Box paddingVertical={12}>
              <Box
                flexDirection="row"
                alignItems="center"
                opacity={currentCoach === 'fide_master' ? 0.5 : 1}>
                {/* Radio Button */}
                {selectedCoach === 'fide_master' ? (
                  <Box
                    width={20}
                    height={20}
                    borderRadius={10}
                    backgroundColor={CCColors.Primary.Brown}
                    justifyContent="center"
                    alignItems="center"
                    marginRight={12}>
                    <MaterialCommunityIcons 
                      name="check" 
                      size={12} 
                      color={CCColors.White} 
                    />
                  </Box>
                ) : (
                  <Box
                    width={20}
                    height={20}
                    borderRadius={10}
                    borderWidth={2}
                    borderColor={CCColors.Grey.Grey}
                    marginRight={12}
                  />
                )}
                
                <CCText 
                  fontSize={FontSizes[16]} 
                  fontWeight="500"
                  color={CCColors.TextColor.Dark}>
                  FIDE Master
                  {currentCoach === 'fide_master' && (
                    currentTag
                  )}
                </CCText>
              </Box>

              {/* Show perks when selected */}
              {selectedCoach === 'fide_master' && (
                <Box marginLeft={32} marginTop={12}>
                  {getPerksForCoach('fide_master').map((perk, index) => (
                    <Box key={index} flexDirection="row" alignItems="center" marginBottom={8}>
                      <Box
                        width={16}
                        height={16}
                        borderRadius={8}
                        backgroundColor={CCColors.Green}
                        justifyContent="center"
                        alignItems="center"
                        marginRight={8}>
                        <MaterialCommunityIcons 
                          name="check" 
                          size={10} 
                          color={CCColors.White} 
                        />
                      </Box>
                      <CCText 
                        fontSize={FontSizes[13]} 
                        color={CCColors.TextColor.Dark}
                        flex={1}>
                        {perk}
                      </CCText>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Pressable>
          </>
          )}

          {/* Grand Masters Option - Only show if available */}
          {availableCoachTiers.includes('grand_master') && (
            <>
              {/* Divider */}
              <Box height={1} backgroundColor={CCColors.Grey.Divider200} />
              
              <Pressable 
              onPress={() => currentCoach !== 'grand_master' && onSelect('grand_master')}
              disabled={currentCoach === 'grand_master'}>
            <Box paddingVertical={12}>
              <Box
                flexDirection="row"
                alignItems="center"
                opacity={currentCoach === 'grand_master' ? 0.5 : 1}>
                {/* Radio Button */}
                {selectedCoach === 'grand_master' ? (
                  <Box
                    width={20}
                    height={20}
                    borderRadius={10}
                    backgroundColor={CCColors.Primary.Brown}
                    justifyContent="center"
                    alignItems="center"
                    marginRight={12}>
                    <MaterialCommunityIcons 
                      name="check" 
                      size={12} 
                      color={CCColors.White} 
                    />
                  </Box>
                ) : (
                  <Box
                    width={20}
                    height={20}
                    borderRadius={10}
                    borderWidth={2}
                    borderColor={CCColors.Grey.Grey}
                    marginRight={12}
                  />
                )}
                
                <Box flex={1}>
                  <CCText 
                    fontSize={FontSizes[16]} 
                    fontWeight="500"
                    color={CCColors.TextColor.Dark}>
                    Grand Masters
                    {currentCoach === 'grand_master' && (
                      currentTag
                    )}
                  </CCText>
                </Box>
              </Box>

              {/* Show perks when selected */}
              {selectedCoach === 'grand_master' && (
                <Box marginLeft={32} marginTop={12}>
                  {getPerksForCoach('grand_master').map((perk, index) => (
                    <Box key={index} flexDirection="row" alignItems="center" marginBottom={8}>
                      <Box
                        width={16}
                        height={16}
                        borderRadius={8}
                        backgroundColor={CCColors.Green}
                        justifyContent="center"
                        alignItems="center"
                        marginRight={8}>
                        <MaterialCommunityIcons 
                          name="check" 
                          size={10} 
                          color={CCColors.White} 
                        />
                      </Box>
                      <CCText 
                        fontSize={FontSizes[13]} 
                        color={CCColors.TextColor.Dark}
                        flex={1}>
                        {perk}
                      </CCText>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Pressable>
          </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const styles = StyleSheet.create({
  // Styles are mostly inline
});

export default CoachSelector;



